import { useEffect, useRef } from 'react';
import { Linking, Platform } from 'react-native';
import {
  completeOAuthFromCallbackUrl,
  isPasswordRecoveryUrl,
  persistWebReturnToFromUrl,
  resolvePasswordRecoveryToPublicWebUrl,
} from '../../auth/application/sso';
import { handleMercadoPagoOAuthReturnUrl, MP_OAUTH_POST_MESSAGE_TYPE } from '../../mercadopago/infra/mercadopagoOAuthCallback';
import { normalizePaidPlanCode, type PaidPlanCode } from '../feature-flags/planIntentService';

export function useDeepLinkRouter({
  onPasswordRecoveryUrl,
  onPlanIntent,
  onBillingFlag,
  onMercadoPagoSettings,
  onSupportTicketId,
}: {
  onPasswordRecoveryUrl: () => void;
  onPlanIntent: (plan: PaidPlanCode) => void;
  onBillingFlag: () => void;
  onMercadoPagoSettings: () => void;
  onSupportTicketId?: (ticketId: string) => void;
}): void {
  const callbacksRef = useRef({
    onPasswordRecoveryUrl,
    onPlanIntent,
    onBillingFlag,
    onMercadoPagoSettings,
    onSupportTicketId,
  });
  callbacksRef.current = {
    onPasswordRecoveryUrl,
    onPlanIntent,
    onBillingFlag,
    onMercadoPagoSettings,
    onSupportTicketId,
  };

  useEffect(() => {
    const handlePlanIntent = (url: string) => {
      try {
        const parsed = new URL(url);
        const plan = normalizePaidPlanCode(parsed.searchParams.get('plan'));
        if (!plan) return;
        if (parsed.searchParams.get('billing') === '1') {
          callbacksRef.current.onBillingFlag();
        }
        callbacksRef.current.onPlanIntent(plan);
      } catch {
        /* ignore */
      }
    };

    const handleSupportTicketLink = (url: string) => {
      try {
        const parsed = new URL(url);
        const ticketId = parsed.searchParams.get('ticketId')?.trim();
        if (!ticketId) return;
        callbacksRef.current.onSupportTicketId?.(ticketId);
      } catch {
        /* ignore */
      }
    };

    const handleDeepLink = (url: string) => {
      if (handleMercadoPagoOAuthReturnUrl(url)) return;
      if (isPasswordRecoveryUrl(url)) {
        if (Platform.OS !== 'web') {
          void Linking.openURL(resolvePasswordRecoveryToPublicWebUrl(url));
          return;
        }
        callbacksRef.current.onPasswordRecoveryUrl();
        return;
      }
      void completeOAuthFromCallbackUrl(url);
      handlePlanIntent(url);
      handleSupportTicketLink(url);
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      const currentIsRecovery = isPasswordRecoveryUrl(currentUrl);
      if (currentIsRecovery) {
        callbacksRef.current.onPasswordRecoveryUrl();
      }
      if (!window.location.pathname.includes('auth/callback')) {
        persistWebReturnToFromUrl();
      }
      if (handleMercadoPagoOAuthReturnUrl(currentUrl)) {
        /* MP OAuth no usa el mismo callback que SSO */
      } else if (!currentIsRecovery) {
        void completeOAuthFromCallbackUrl(currentUrl);
      }
      handlePlanIntent(currentUrl);
      handleSupportTicketLink(currentUrl);
    }

    const sub = Linking.addEventListener('url', (event) => handleDeepLink(event.url));
    Linking.getInitialURL().then((url) => {
      if (url) handleDeepLink(url);
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== MP_OAUTH_POST_MESSAGE_TYPE || typeof event.data.url !== 'string') return;
      handleMercadoPagoOAuthReturnUrl(event.data.url);
      callbacksRef.current.onMercadoPagoSettings();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
}
